import { z } from "zod";
import { MarketplaceAssetType, MarketplaceOfferingType, } from "../AltAssetsService/Types";
import { ClientId, DateSchema, LocalDate, MarketplaceOfferingId, OpportunityId, } from "../Types";
// NOTE: it's better to let TS infer the return type here
/* eslint-disable-next-line @typescript-eslint/explicit-function-return-type */
export const VersionedValue = (schema) => z.object({
    value: schema,
    version: z.literal(1),
});
export const OpportunityClient = z.object({
    clientId: ClientId,
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
});
export const OpportunityAccount = z.object({
    accountName: z.string().nullable(),
    accountType: z.string().nullable(),
    accountBalance: z.number().nullable(),
    accountInstitution: z.string().nullable(),
});
export const OpportunityTransaction = z.object({
    amount: z.number(),
    transactionId: z.string().uuid(),
    transactionDate: LocalDate,
    transactionName: z.string(),
});
export const OpportunityOfferingInterest = z.enum([
    "INTEREST",
    "SCHEDULED_MEETING",
]);
const OpportunityOfferingBase = z.object({
    offeringId: MarketplaceOfferingId,
    offeringType: MarketplaceOfferingType,
    assetType: MarketplaceAssetType,
    interestType: OpportunityOfferingInterest,
});
export const OpportunityMarketplaceOffering = OpportunityOfferingBase.extend({
    partnerName: z.string(),
});
export const OpportunityAltAssetsOffering = OpportunityOfferingBase.extend({
    offeringName: z.string(),
    firm: z.string().nullable(),
    platform: z.string().nullable(),
    objective: z.string().nullable(),
    strategy: z.string().nullable(),
    substrategy: z.string().nullable(),
});
export const OpportunityStatus = z.enum([
    "NOT_STARTED",
    "IN_PROGRESS",
    "AWAITING_RESPONSE",
    "SNOOZED",
    "CANCELED",
    "COMPLETED_WITH_ASSETS_WON",
    "COMPLETED_NO_ASSETS_WON",
]);
export const OpportunityType = z.enum([
    "LARGE_DEPOSIT",
    "LARGE_BANK_ACCOUNT_BALANCE",
    "LARGE_INVESTMENT_ACCOUNT_BALANCE",
    "INSURANCE_INTEREST",
    "LENDING_INTEREST",
    "ALTERNATIVE_ASSET_INTEREST",
]);
export const OpportunityPriority = z.enum(["LOW", "MEDIUM", "HIGH"]);
export const OpportunitySnoozingPeriod = z.enum([
    "OneWeek",
    "OneMonth",
    "ThreeMonths",
    "SixMonths",
]);
export const OpportunityCancelationReason = z.enum([
    "IncorrectData",
    "DuplicateOpportunity",
    "DuplicatedBySharedAccount",
    "OtherCancelReason",
]);
export const OpportunityCompletionReason = z.enum([
    "ClientNotInterested",
    "OtherReason",
]);
export const ListOpportunity = z.object({
    id: OpportunityId,
    client: OpportunityClient,
    createdTs: DateSchema,
    updatedTs: DateSchema,
    dueDate: LocalDate.nullable(),
    type: OpportunityType,
    status: OpportunityStatus,
    priority: OpportunityPriority,
});
export const BaseOpportunity = ListOpportunity.omit({ client: true }).extend({
    clientDetails: OpportunityClient.extend({
        email: z.string(),
        createdTs: DateSchema,
        modifiedTs: DateSchema,
        advisorName: z.string(),
    }),
});
/**
 * Opportunity type combinations
 */
export const LargeDepositOpportunity = BaseOpportunity.extend({
    type: z.literal(OpportunityType.enum.LARGE_DEPOSIT),
    typeAttributes: VersionedValue(OpportunityAccount.merge(OpportunityTransaction)),
});
export const LargeBalanceOpportunity = BaseOpportunity.extend({
    type: z.union([
        z.literal(OpportunityType.enum.LARGE_BANK_ACCOUNT_BALANCE),
        z.literal(OpportunityType.enum.LARGE_INVESTMENT_ACCOUNT_BALANCE),
    ]),
    typeAttributes: VersionedValue(OpportunityAccount),
});
export const MarketplaceOfferingInterestOpportunity = z.union([
    BaseOpportunity.extend({
        type: z.union([
            z.literal(OpportunityType.enum.INSURANCE_INTEREST),
            z.literal(OpportunityType.enum.LENDING_INTEREST),
        ]),
        typeAttributes: VersionedValue(OpportunityMarketplaceOffering),
    }),
    BaseOpportunity.extend({
        type: z.literal(OpportunityType.enum.ALTERNATIVE_ASSET_INTEREST),
        typeAttributes: VersionedValue(OpportunityAltAssetsOffering),
    }),
]);
/**
 * Opportunity status combinations
 */
export const NotStartedOpportunity = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.NOT_STARTED),
    statusAttributes: VersionedValue(z.literal(null)),
});
export const InProgressOpportunity = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.IN_PROGRESS),
    statusAttributes: VersionedValue(z.literal(null)),
});
export const AwaitingResponseOpportunity = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.AWAITING_RESPONSE),
    statusAttributes: VersionedValue(z.literal(null)),
});
export const SnoozedOpportunity = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.SNOOZED),
    statusAttributes: VersionedValue(z.object({
        period: OpportunitySnoozingPeriod,
        details: z.string(),
        extraDetails: z.string().nullable(),
    })),
});
export const StandardReasonCanceledOpportunity = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.CANCELED),
    statusAttributes: VersionedValue(z.object({
        reason: OpportunityCancelationReason.exclude(["OtherCancelReason"]),
        extraDetails: z.string().nullable(),
    })),
});
export const OtherReasonCanceledOpportunity = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.CANCELED),
    statusAttributes: VersionedValue(z.object({
        reason: z.literal(OpportunityCancelationReason.enum.OtherCancelReason),
        details: z.string(),
        extraDetails: z.string().nullable(),
    })),
});
export const CanceledOpportunity = z.union([
    StandardReasonCanceledOpportunity,
    OtherReasonCanceledOpportunity,
]);
export const CompletedOpportunityWithAssetsWon = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.COMPLETED_WITH_ASSETS_WON),
    statusAttributes: VersionedValue(z.object({
        amount: z.number(),
        expectedClearanceDate: LocalDate,
        extraDetails: z.string().nullable(),
    })),
});
export const StandardReasonCompletedOpportunityNoAssetsWon = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.COMPLETED_NO_ASSETS_WON),
    statusAttributes: VersionedValue(z.object({
        reason: OpportunityCompletionReason.exclude(["OtherReason"]),
        extraDetails: z.string().nullable(),
    })),
});
export const OtherReasonCompletedOpportunityNoAssetsWon = BaseOpportunity.extend({
    status: z.literal(OpportunityStatus.enum.COMPLETED_NO_ASSETS_WON),
    statusAttributes: VersionedValue(z.object({
        reason: z.literal(OpportunityCompletionReason.enum.OtherReason),
        details: z.string(),
        extraDetails: z.string().nullable(),
    })),
});
export const CompletedOpportunityNoAssetsWon = z.union([
    StandardReasonCompletedOpportunityNoAssetsWon,
    OtherReasonCompletedOpportunityNoAssetsWon,
]);
export const Opportunity = z.intersection(z.union([
    LargeDepositOpportunity,
    LargeBalanceOpportunity,
    MarketplaceOfferingInterestOpportunity,
]), z.union([
    NotStartedOpportunity,
    InProgressOpportunity,
    AwaitingResponseOpportunity,
    SnoozedOpportunity,
    CanceledOpportunity,
    CompletedOpportunityWithAssetsWon,
    CompletedOpportunityNoAssetsWon,
]));
//# sourceMappingURL=Types.js.map